import React, { useState } from 'react';
import { Formik, Form } from 'formik';

import { api } from '../../../core/services';
import { Visible } from '../../../core/components';
import { FieldGroup, SubmitButton } from '../../../components';
import { Button } from '../../../components/ui';

import { formConfig, productFields, initialValues, initialValuesFromSubmission, validationSchema } from './productFormConfig';

/**
 * Create a catalogue product — or, when `product` is given, EDIT an existing
 * store submission (store-catalog feature: owner/master may edit until it is
 * approved) via PUT /api/store-catalog/products/{id}.
 *
 * `fontaineControls` is opt-in and OFF by default: only the /add-new-product
 * route view passes it, so the modal's edit form keeps the legacy SubmitButton.
 */
const AddProductForm = ({ categoryId, onSuccess, product = null, submitLabel = 'Save', forStore = false, fontaineControls = false }) => {
  const [saveError, setSaveError] = useState(null);

  const handleSubmit = async (data) => {
    setSaveError(null);

    const submitData = {
      category_id: categoryId,
      brand_id: data.brand_id,
      model_id: data.model_id,
      reference_number: data.reference_number,
      images: data.images,
    };

    // Opened from the store-facing surface (Owner Admin → My Products, which
    // navigates here with ?for_store=1): an RC master acting as a store then
    // submits FOR that store instead of publishing to the shared catalogue.
    // Only meaningful on create — an edit already belongs to a store.
    if (forStore && !product) {
      submitData.for_store = true;
    }

    submitData.values = productFields.reduce((values, field) => ({
      ...values,
      [field.name]: data[field.name],
    }), {});

    if (submitData.values.price_gbp) {
      submitData.values.price_gbp = Math.round(parseFloat(submitData.values.price_gbp) * 100);
    }
    if (submitData.values.price_euro) {
      submitData.values.price_euro = Math.round(parseFloat(submitData.values.price_euro) * 100);
    }

    try {
      // Pass the created/updated product up — store submissions come back
      // with approval_status 'pending' (store-catalog feature) and the
      // success screen tells the owner where to track it.
      const result = product
        ? await api.put(`/api/store-catalog/products/${product.id}`, submitData)
        : await api.post('/api/products', submitData);
      onSuccess(result);
    } catch (error) {
      setSaveError(`The product could not be saved. ${error?.error || error.message}`);
    }
  }

  return (
    <div>
      <Formik
        {...{
          initialValues: product ? initialValuesFromSubmission(product) : initialValues,
          validationSchema,
          onSubmit: handleSubmit,
        }}
      >
        {({
          values: { brand_id: brandId },
          setFieldValue,
          isSubmitting,
          isValid,
          submitCount,
        }) => (
          <Form>
            <FieldGroup
              {...{
                ...formConfig.brand,
                optionsUrl: formConfig.brand.optionsUrl(categoryId),
                onChange: value => {
                  setFieldValue('brand_id', value);
                  setFieldValue('model_id', null);
                },
              }}
            />
            <FieldGroup
              {...{
                ...formConfig.model,
                optionsUrl: brandId && formConfig.model.optionsUrl(categoryId, brandId),
                onChange: value => setFieldValue('model_id', value),
              }}
            />
            <FieldGroup {...formConfig.referenceNumber} />

            {formConfig.productFields.map(field => (
              <FieldGroup {...field} key={field.name} />
            ))}

            <FieldGroup {...formConfig.images} />

            <Visible when={(submitCount > 0 && !isValid) || saveError}>
              <div className="error text-right">
                {(submitCount > 0 && !isValid) && 'Some fields are not valid'}
                {saveError}
              </div>
            </Visible>

            <div className="mt-4 text-right add-product-form__actions">
              {fontaineControls
                ? (
                  // `disabled` is stated as well as `loading` so the native
                  // control is inert, exactly as SubmitButton's was.
                  <Button type="submit" size="lg" variant="primary" loading={isSubmitting} disabled={isSubmitting}>
                    {submitLabel}
                  </Button>
                )
                : <SubmitButton label={submitLabel} isSubmitting={isSubmitting} />}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default AddProductForm;
